// API base URL - set via VITE_API_URL environment variable during build
const API_BASE = import.meta.env.VITE_API_URL ?? "";

// Token storage keys
const ACCESS_TOKEN_KEY = "af_access_token";
const REFRESH_TOKEN_KEY = "af_refresh_token";

// ════════════════════════════════════════════════════════════════════════════
// Token Management
// ════════════════════════════════════════════════════════════════════════════

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

// Returns a valid access token, proactively refreshing if expired or expiring
// within 60 seconds. Use this in streaming transports that bypass api().
export async function getOrRefreshToken(): Promise<string | null> {
  const token = getAccessToken();
  if (!token) return null;

  try {
    const parts = token.split(".");
    if (parts.length === 3) {
      const payload = JSON.parse(atob(parts[1]!.replace(/-/g, "+").replace(/_/g, "/")));
      const exp = payload.exp as number | undefined;
      const secsUntilExpiry = exp ? exp - Math.floor(Date.now() / 1000) : Infinity;
      if (secsUntilExpiry < 60) {
        const refreshed = await doRefreshToken();
        return refreshed ? getAccessToken() : null;
      }
    }
  } catch {
    // Unparseable token — let the server reject it
  }

  return token;
}

export function setTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

// ════════════════════════════════════════════════════════════════════════════
// API Client
// ════════════════════════════════════════════════════════════════════════════

// Refresh lock to prevent race conditions when multiple requests get 401
let refreshPromise: Promise<boolean> | null = null;

interface ApiOptions extends RequestInit {
  skipAuth?: boolean;
}

export class ApiError extends Error {
  status: number;
  /**
   * Optional second-level context from the backend response body.
   * Backends may return `{ error: "Human summary", details: "Tech detail" }`.
   * When present, this field carries the `details` value so callers can
   * choose to surface both for debugging.
   */
  details?: string;

  constructor(message: string, status: number, details?: string) {
    super(message);
    this.status = status;
    this.name = "ApiError";
    this.details = details;
  }
}

export async function api<T>(
  path: string,
  options?: ApiOptions
): Promise<T> {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...options?.headers,
  };

  // Add auth token if available and not skipped
  if (!options?.skipAuth) {
    const token = getAccessToken();
    if (token) {
      (headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
    }
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  // Handle 401 - try to refresh token
  if (res.status === 401 && !options?.skipAuth) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      // Retry the request with new token
      const newToken = getAccessToken();
      if (newToken) {
        (headers as Record<string, string>)["Authorization"] = `Bearer ${newToken}`;
      }
      const retryRes = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers,
      });

      if (!retryRes.ok) {
        const error = await retryRes
          .json()
          .catch(() => ({ error: retryRes.statusText }));
        throw new ApiError(
          error.error ?? `API error: ${retryRes.status}`,
          retryRes.status,
          typeof error.details === "string" ? error.details : undefined
        );
      }
      return retryRes.json();
    }
    // Refresh failed, clear tokens
    clearTokens();
    window.location.href = "/login";
    throw new ApiError("Session expired", 401);
  }

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(
      error.error ?? `API error: ${res.status}`,
      res.status,
      typeof error.details === "string" ? error.details : undefined
    );
  }

  return res.json();
}

async function tryRefreshToken(): Promise<boolean> {
  // If already refreshing, wait for that result to avoid race conditions
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = doRefreshToken();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

async function doRefreshToken(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  try {
    const res = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });

    if (!res.ok) return false;

    const data = await res.json();
    localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
    return true;
  } catch {
    return false;
  }
}

// ════════════════════════════════════════════════════════════════════════════
// Auth API Functions
// ════════════════════════════════════════════════════════════════════════════

export interface LoginResponse {
  user: {
    id: string;
    email: string;
    name: string | null;
    role: string;
    isPlatformUser?: boolean;
  };
  accessToken: string;
  refreshToken: string;
}

export async function login(email: string, password: string, tenantSlug?: string): Promise<LoginResponse> {
  const body: { email: string; password: string; tenantSlug?: string } = { email, password };
  if (tenantSlug) {
    body.tenantSlug = tenantSlug;
  }

  const response = await api<LoginResponse>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify(body),
    skipAuth: true,
  });

  setTokens(response.accessToken, response.refreshToken);
  return response;
}

export async function logout(): Promise<void> {
  clearTokens();
}

export async function getCurrentUser(): Promise<LoginResponse["user"] | null> {
  const token = getAccessToken();
  if (!token) return null;

  try {
    const response = await api<{ user: LoginResponse["user"] }>("/api/auth/me");
    return response.user;
  } catch {
    return null;
  }
}
